import React, { useEffect, useState } from "react";

import { TFunction, goTo, useNavigate, useTranslation, useUserContext } from "utils";

import { CROP_EXPLORER_RESULT_CALCULATION_TYPE, CROP_EXPLORER_RESULT_TYPE, CropExplorationResponse, User, YearRange } from "models";

import { Button, Divider, ExtraInfo, Flex, IconLinkButton, KlimRadioGroup } from "components";

import { CropExplorationContextType, useCropExplorationContext } from "pages/app/crop-explorer/CropExplorationContext";
import { CropExplorerResultCard } from "pages/app/crop-explorer/components/CropExplorerResultCard";

enum ENTRY_TYPE {
  BAU = "BAU",
  YEARLY = "YEARLY",
}

type CropExplorationContextCalculation = CropExplorationContextType & { cropExploration: CropExplorationResponse };

export const CropExplorationSidebar = () => {
  const { t } = useTranslation(["cropExplorer"]);
  const { user } = useUserContext();
  const [yearlyYearRange, setYearlyYearRange] = useState<YearRange>({});
  const [showPayoutDrawer, setShowPayoutDrawer] = useState(false);
  const [showEmissionDrawer, setShowEmissionDrawer] = useState(false);
  const [toggleOption, setToggleOption] = useState<CROP_EXPLORER_RESULT_CALCULATION_TYPE>(CROP_EXPLORER_RESULT_CALCULATION_TYPE.TOTAL);

  const navigate = useNavigate();

  const footerButtonDynamicLink = getFooterButtonDynamicLink(user, t);

  const { cropExploration } = useCropExplorationContext() as CropExplorationContextCalculation;

  const options = [
    { label: t("sidebar.total"), value: CROP_EXPLORER_RESULT_CALCULATION_TYPE.TOTAL },
    { label: t("sidebar.perHa"), value: CROP_EXPLORER_RESULT_CALCULATION_TYPE.PER_HA },
  ];

  const isShowingEmissionsResults =
    cropExploration?.calculation?.emissions.yearlyTotals.some((result) => cropExploration.bauYears.includes(result.year)) || false;
  const bauYearRange = getYearRange(ENTRY_TYPE.BAU, cropExploration);

  useEffect(() => {
    if (!cropExploration) return;
    const yearRange = getYearRange(ENTRY_TYPE.YEARLY, cropExploration);
    setYearlyYearRange(yearRange);
  }, [cropExploration]);

  return (
    <Flex className="crop-explorer-sidebar container" vertical={true}>
      <div className="crop-explorer-sidebar header">
        <p data-testid="crop-exploration-side-bar-header-title-year" className="crop-explorer-sidebar header year">
          {formatYearRange(yearlyYearRange, t)}
        </p>
        <Flex justify="space-between" align="end">
          <p data-testid="crop-exploration-side-bar-header-title" className="crop-explorer-sidebar header title">
            {t("cropExplorer:sidebar.forecast")}
          </p>
          <KlimRadioGroup
            type="other"
            size="middle"
            dataTestId="crop-explorer-result-toggle"
            options={options}
            onChange={(e) => setToggleOption(e.target.value)}
            value={toggleOption}
            optionType="button"
            buttonStyle="solid"
          />
        </Flex>
      </div>
      <Divider />
      <div className="crop-explorer-sidebar body">
        <Flex vertical={true}>
          <IconLinkButton
            dataTestId="crop-explorer-payout-info-icon-button"
            theme="grey"
            iconType="info"
            size="small"
            text={t("sidebar.potentialPayout")}
            onClick={() => setShowPayoutDrawer(true)}
          />
          <CropExplorerResultCard
            resultType={CROP_EXPLORER_RESULT_TYPE.PAYOUT}
            calculationType={toggleOption}
            calculation={cropExploration?.calculation}
            showResults={!!yearlyYearRange?.start}
          />
        </Flex>
        <Flex vertical={true} className="margin-top-100">
          <IconLinkButton
            dataTestId="crop-explorer-emission-info-icon-button"
            theme="grey"
            iconType="info"
            size="small"
            text={t("sidebar.potentialEmission")}
            onClick={() => setShowEmissionDrawer(true)}
          />
          <CropExplorerResultCard
            resultType={CROP_EXPLORER_RESULT_TYPE.EMISSION}
            calculationType={toggleOption}
            calculation={cropExploration?.calculation}
            showResults={isShowingEmissionsResults}
            yearRange={bauYearRange}
          />
        </Flex>
      </div>
      <Divider />
      <Flex className="crop-explorer-sidebar footer">
        <Button
          dataTestId={`crop-explorer-footer-button-${footerButtonDynamicLink?.dataTestId}`}
          type="primary"
          htmlType="submit"
          loading={false}
          onClick={() => goTo(null, navigate, footerButtonDynamicLink?.link)}
        >
          {footerButtonDynamicLink?.label}
        </Button>
      </Flex>
      <ExtraInfo setVisible={setShowPayoutDrawer} type="crop-explorer-payout" visible={showPayoutDrawer} />
      <ExtraInfo setVisible={setShowEmissionDrawer} type="crop-explorer-emission" visible={showEmissionDrawer} />
    </Flex>
  );
};

const getYearRange = (type: ENTRY_TYPE, cropExploration?: CropExplorationResponse | null): YearRange => {
  if (!cropExploration) return {};

  // check if there is a main crop for each bau
  const completeBauEntries = cropExploration.bauEntries.filter((bauEntry) => bauEntry.main !== null);
  if (completeBauEntries.length < 3) return {};

  if (type === ENTRY_TYPE.BAU) {
    return {
      start: Math.min(...cropExploration.bauYears),
      end: Math.max(...cropExploration.bauYears),
    };
  }

  // foreach yearly data check if there is a main crop
  const bauYears = completeBauEntries.map((bauEntry) => bauEntry.year);
  const sortedBauYears = bauYears.sort((a, b) => a - b);
  const lastBauYear = sortedBauYears[2];

  const completeYearlyEntries = cropExploration.yearlyEntries.filter((yearlyEntry) => yearlyEntry.main !== null);
  const years = completeYearlyEntries.map((yearlyEntry) => yearlyEntry.year);
  const sortedYears = years.sort((a, b) => a - b);

  const continuesYears = getContinuousYears(sortedYears, lastBauYear);

  const firstYear = continuesYears[0];
  const lastYear = continuesYears[continuesYears.length - 1];

  if (!firstYear) return {};
  if (firstYear === lastYear) return { start: firstYear };

  return { start: firstYear, end: lastYear };
};

const getContinuousYears = (years: number[], lastBaselineYear: number): number[] => {
  const continuesYears: number[] = [];
  let isContinues: boolean = true;
  if (years[0] - 1 !== lastBaselineYear) return [];

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < years.length; i++) {
    if (i === 0) {
      continuesYears.push(years[i]);
      continue; // eslint-disable-line no-continue
    }
    const previousYear = years[i - 1];
    if (years[i] - 1 === previousYear) continuesYears.push(years[i]);
    else isContinues = false;
  }

  return isContinues ? continuesYears : [];
};

const formatYearRange = (yearRange: YearRange, t: TFunction<string[], undefined>): string => {
  if (!yearRange.start) return "";
  if (yearRange.start && !yearRange.end) return `${yearRange.start}`;
  if (yearRange.start && yearRange.end) return `${yearRange.start} ${t("sidebar.to")} ${yearRange.end}`;
  return "";
};

const getFooterButtonDynamicLink = (user: User | null, t: (key: string) => string): { dataTestId: string; link: string; label: string } | null => {
  if (!user) {
    return {
      dataTestId: "sign-up",
      link: "/sign-up",
      label: t("resultFooterButton.signUp"),
    };
  }
  if (!user?.farm) return null;

  if (!user.farm.farmOnboarded) {
    return {
      dataTestId: "add-farm",
      link: "/dashboard?onboarding=true",
      label: t("resultFooterButton.addFarm"),
    };
  }

  if (user.farm.farmOnboarded && user.farm.fields === 0) {
    return {
      dataTestId: "add-fields",
      link: "/dashboard?onboarding=true",
      label: t("resultFooterButton.addFields"),
    };
  }

  return null;
};
